import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

interface UserWithEmail {
  id: string;
  email: string;
  role: string;
  tokens: number;
  ai_usage_today: number;
  ai_usage_reset_date: string;
  ai_daily_usage: number;
  ai_advice: string;
  mfa_enabled: boolean;
  account_type: string;
  /** Real suspension flag — sourced from auth.users.user_metadata.suspended
   *  via the admin-users-list edge function. `null` means we couldn't
   *  reach the edge function (treat as unknown, not "active"); was
   *  previously faked from mfa_enabled which made the suspend UX
   *  backwards. */
  is_suspended: boolean | null;
  created_at: string;
  updated_at: string;
}

export function useAdmin() {
  const { user } = useAuth();

  // Check if user has admin role using the secure user_roles table
  const { data: userRoles, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["user_roles", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    staleTime: 0, // Always refetch to get latest roles
    refetchOnMount: 'always',
  });

  const isAdmin = userRoles?.some(r => r.role === "admin" || r.role === "superuser") || false;
  const isLoading = isLoadingRoles;

  const { data: users, isLoading: isLoadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ["admin_users"],
    queryFn: async (): Promise<UserWithEmail[]> => {
      // First get profiles
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const nowIso = new Date().toISOString();
      const { data: entitlements, error: entitlementsError } = await supabase
        .from("entitlements")
        .select("user_id,feature,expires_at")
        .eq("active", true)
        .in("feature", ["premium", "lifetime"]);
      if (entitlementsError) throw entitlementsError;
      const premiumUserIds = new Set(
        (entitlements ?? [])
          .filter((entitlement) => !entitlement.expires_at || entitlement.expires_at > nowIso)
          .map((entitlement) => entitlement.user_id),
      );
      
      // Get user emails + suspended flag from edge function. If the
      // call fails we surface the failure (toast + null is_suspended)
      // rather than silently treating every user as not-suspended —
      // that would hide a real outage AND make the Users-page
      // suspended stat look "all good" when in fact we don't know.
      const userIds = profiles?.map(p => p.id) || [];
      let emailMap: Record<string, string> = {};
      let suspendedMap: Record<string, boolean> = {};
      let edgeFailed = false;

      if (userIds.length > 0) {
        try {
          const { data: edgeData } = await supabase.functions.invoke('admin-users-list', {
            body: { userIds }
          });
          emailMap = edgeData?.emails || {};
          suspendedMap = edgeData?.suspended || {};
        } catch (e) {
          console.error('Failed to fetch user emails:', e);
          edgeFailed = true;
          // English-only toast since this hook has no i18n context and
          // it only ever fires for admins (small surface, dev-facing).
          toast.error('Could not load user emails / suspended state');
        }
      }

      return (profiles || []).map(p => ({
        ...p,
        account_type: premiumUserIds.has(p.id) ? "premium" : "free",
        email: emailMap[p.id] || `User ${p.id.slice(0, 8)}`,
        is_suspended: edgeFailed ? null : suspendedMap[p.id] === true,
      }));
    },
    enabled: isAdmin,
  });

  const { data: exercises, isLoading: isLoadingExercises, refetch: refetchExercises } = useQuery({
    queryKey: ["admin_exercises"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exercises")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: isAdmin,
  });

  const { data: marketplaceItems, isLoading: isLoadingMarketplace, refetch: refetchMarketplace } = useQuery({
    queryKey: ["admin_marketplace"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketplace_items")
        .select(`
          *,
          programs (
            title,
            description
          )
        `)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: isAdmin,
  });

  return {
    isAdmin,
    isLoading,
    users,
    isLoadingUsers,
    refetchUsers,
    exercises,
    isLoadingExercises,
    refetchExercises,
    marketplaceItems,
    isLoadingMarketplace,
    refetchMarketplace,
  };
}
